import {
  Card,
  CardBody,
  CardHeader,
  Table as ChakraTable,
  TableContainer,
  Tbody,
  Th,
  Thead,
  Tr,
  Td,
  Text,
  Divider,
  Icon,
  Flex,
  Button,
  Link,
  Box,
} from '@chakra-ui/react';
import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import { Toggle } from 'rsuite';
import {
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Tag,
  Spinner,
} from '@spotoninc/nexus-react';
import {
  ThumbsUpIcon,
  ChartLinesIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@spotoninc/nexus-web-assets';
import { useFeatureFlagFromContext } from '../../../launchdarkly';
import '../../../styles/nexus-table.css';

const TabularCard = ({
  data,
  bgColor,
  headerIcon,
  header,
  icon,
  clickThru,
  headerColumnText = 'Header', // Default to 'Header' if not provided
}) => {
  // Check if the reskin feature flag is enabled
  const showReskin = useFeatureFlagFromContext('showReskin', false);

  // State hooks to replace class component state
  const [showOnlyTen, setShowOnlyTen] = useState(true); // State to track whether to show only 10 rows or all
  const [sortByHeader, setSortByHeader] = useState(null); // Sorting state for the "Header" column (null = not sorted)
  const [sortByPercentage, setSortByPercentage] = useState(null); // Sorting state for percentage column
  const [sortByChange, setSortByChange] = useState(null); // Sorting state for dollar change column

  // State for infinite scrolling in Nexus Table
  const [itemsToShow, setItemsToShow] = useState(20); // Start with 20 items for better initial view
  const [isLoadingMore, setIsLoadingMore] = useState(false); // Loading state for infinite scroll
  const observerTarget = useRef(null); // Reference for the intersection observer
  const prevDataLength = useRef(0); // Track previous data length to avoid unnecessary updates

  // State for loading and no data message
  const [minLoadingTimeElapsed, setMinLoadingTimeElapsed] = useState(false); // Track if minimum loading time has elapsed
  const [showNoData, setShowNoData] = useState(false); // Track if we should show "No Data" message
  const [readyToShowNoData, setReadyToShowNoData] = useState(false); // Track if we're ready to show "No Data" message after minimum loading time
  const [isDataAvailable, setIsDataAvailable] = useState(false); // Track if data is available to display

  // Get chartCurrency from Redux store
  const chartCurrency = useSelector(
    state => state.locationSelectFormat.currency
  );

  // Determine header background color based on headerColumnText
  const getHeaderBgColor = () => {
    if (headerColumnText === 'Wins') {
      return '#e8f7ea'; // Light green for success
    } else if (headerColumnText === 'Losses') {
      return '#fff0ef'; // Light red for danger
    }
    return 'transparent';
  };

  // Determine if we should apply success-light background to per_change
  const isWinsTable = headerColumnText === 'Wins';

  // Function to toggle the rows (10 or all)
  const handleToggle = () => {
    setShowOnlyTen(!showOnlyTen); // Toggle between showing 10 and all
  };

  // Function to toggle sort state (asc -> desc) and reset the other column's sort state
  const toggleSort = column => {
    // Determine the new sorting state (alternate between asc and desc)
    let newState;
    if (column === 'sortByHeader') {
      if (sortByHeader === 'desc') {
        newState = 'asc';
      } else {
        newState = 'desc';
      }
      setSortByHeader(newState);
      setSortByPercentage(null);
      setSortByChange(null);
    } else if (column === 'sortByPercentage') {
      if (sortByPercentage === 'desc') {
        newState = 'asc';
      } else {
        newState = 'desc';
      }
      setSortByHeader(null);
      setSortByPercentage(newState);
      setSortByChange(null);
    } else if (column === 'sortByChange') {
      if (sortByChange === 'desc') {
        newState = 'asc';
      } else {
        newState = 'desc';
      }
      setSortByHeader(null);
      setSortByPercentage(null);
      setSortByChange(newState);
    }

    // Column sorting state has been updated
  };

  // Function to sort data based on the column and sort state
  const sortData = (data, column, sortState, isString = false) => {
    if (!sortState) return data; // If not sorted, return the data as is
    return data.slice().sort((a, b) => {
      if (isString) {
        if (sortState === 'asc') {
          return a[column].localeCompare(b[column]);
        } else {
          return b[column].localeCompare(a[column]);
        }
      } else {
        if (sortState === 'asc') {
          return a[column] - b[column];
        } else {
          return b[column] - a[column];
        }
      }
    });
  };

  // Sort the data based on the current sort state - using useMemo to optimize performance
  const displayedData = useMemo(() => {
    if (!data) return [];

    // Apply sorting in sequence
    let sortedData = sortData(data, 'name', sortByHeader, true); // Sorting by header (string)
    sortedData = sortData(sortedData, 'per_change', sortByPercentage);
    sortedData = sortData(sortedData, 'change', sortByChange);

    // For Nexus Table with infinite scrolling, use itemsToShow
    // For the original Chakra table, use the showOnlyTen logic
    return showReskin
      ? sortedData.slice(0, itemsToShow)
      : showOnlyTen
        ? sortedData.slice(0, 10)
        : sortedData;
  }, [
    data,
    sortByHeader,
    sortByPercentage,
    sortByChange,
    showOnlyTen,
    showReskin,
    itemsToShow,
  ]);

  // Intersection Observer setup for infinite scrolling
  const handleObserver = useCallback(
    entries => {
      const [entry] = entries;
      // Only load more if we're intersecting, have data, and haven't shown all items yet
      if (entry.isIntersecting && data && data.length > itemsToShow) {
        setIsLoadingMore(true);
        // Load more items immediately - no artificial delay
        // Load 20 items at once to reduce the frequency of updates
        setItemsToShow(prev => Math.min(prev + 20, data.length));
        setIsLoadingMore(false);
      }
    },
    [itemsToShow, data]
  );

  // Set up the intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '100px', // Increase rootMargin to start loading earlier
      threshold: 0.1,
    });

    // Store the current value of the ref to use in the cleanup function
    const currentObserverTarget = observerTarget.current;

    if (currentObserverTarget && showReskin) {
      observer.observe(currentObserverTarget);
    }

    return () => {
      if (currentObserverTarget) {
        observer.unobserve(currentObserverTarget);
      }
    };
  }, [handleObserver, showReskin]);

  // Reset items to show when data or sorting changes
  useEffect(() => {
    // Only reset if data actually changed (different length or different source)
    if (!data || prevDataLength.current !== (data?.length || 0)) {
      setItemsToShow(20); // Start with 20 items
      prevDataLength.current = data?.length || 0;
      // Data is available
    } else if (sortByHeader || sortByPercentage || sortByChange) {
      // If only sorting changed, maintain the current number of items shown
      // but ensure we're at the top of the table
      if (tableBodyRef.current) {
        tableBodyRef.current.scrollTop = 0;
      }
    }
  }, [data, sortByHeader, sortByPercentage, sortByChange]);

  // Reset loading state when data changes (e.g., when location changes)
  useEffect(() => {
    // Reset loading states when data changes
    setMinLoadingTimeElapsed(false);
    setShowNoData(false);
    setReadyToShowNoData(false);

    // If there's no data, start the minimum loading time timer
    if (!(data && data.length > 0)) {
      // Always show spinner first for at least 3 seconds before showing "No Data"
      const timer = setTimeout(() => {
        setMinLoadingTimeElapsed(true);
      }, 3000); // Wait at least 3 seconds before allowing "No Data" message

      return () => clearTimeout(timer);
    }
  }, [data]); // Reset when data changes

  // Determine if we should show "No Data" after minimum loading time
  useEffect(() => {
    // Always update showNoData state when data changes
    // Only show "No Data" when data array is empty (length === 0)
    // but not when data is undefined/null or has items
    const hasNoData = data && data.length === 0;
    const hasData = data && data.length > 0;

    setShowNoData(hasNoData);
    setIsDataAvailable(hasData);
  }, [data]);

  // Determine if we're ready to show "No Data" message after minimum loading time
  useEffect(() => {
    if (minLoadingTimeElapsed && showNoData) {
      setReadyToShowNoData(true);
    } else if (!showNoData) {
      setReadyToShowNoData(false);
    }
  }, [minLoadingTimeElapsed, showNoData]);

  // Reference for the table body container to scroll to top
  const tableBodyRef = useRef(null);

  // Function to scroll the table back to top
  const scrollTableToTop = () => {
    if (tableBodyRef.current) {
      tableBodyRef.current.scrollTop = 0;
    }
  };

  // Function to prevent scroll propagation
  const handleWheel = useCallback(event => {
    const container = tableBodyRef.current;
    if (!container) return;

    const { deltaY, currentTarget } = event;
    const { scrollTop, scrollHeight, clientHeight } = currentTarget;

    // Check if we're at the top and trying to scroll up, or at the bottom and trying to scroll down
    const isAtTop = scrollTop === 0;
    const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 1;

    // If we're at the top and trying to scroll up, or at the bottom and trying to scroll down, prevent propagation
    if ((isAtTop && deltaY < 0) || (isAtBottom && deltaY > 0)) {
      event.preventDefault();
    } else {
      // Otherwise, stop propagation but allow the scroll within the container
      event.stopPropagation();
    }
  }, []);

  // Render the Nexus Table when showReskin is true
  const renderNexusTable = () => (
    <div className="custom-nexus-table-styles">
      <Box className="nexus-table-container">
        <Table>
          <div className="nexus-table-header">
            <TableHead>
              <TableRow variant="filled">
                <TableHeaderCell
                  className={`TableHeaderCell ${headerColumnText === 'Wins' ? 'TableHeaderCell--wins-header' : headerColumnText === 'Losses' ? 'TableHeaderCell--losses-header' : ''}`}
                  style={{ backgroundColor: getHeaderBgColor() }}
                  onClick={() => {
                    toggleSort('sortByHeader');
                    scrollTableToTop();
                  }}
                >
                  <Flex
                    alignItems="center"
                    justifyContent="flex-start"
                    width="100%"
                  >
                    {headerColumnText === 'Wins' ? (
                      <>
                        <ThumbsUpIcon
                          size="16px"
                          className="wins-header-icon"
                        />
                        <span className="wins-header-text">
                          {headerColumnText}
                        </span>
                      </>
                    ) : headerColumnText === 'Losses' ? (
                      <>
                        <ChartLinesIcon
                          size="16px"
                          className="losses-header-icon"
                        />
                        <span className="losses-header-text">
                          {headerColumnText}
                        </span>
                      </>
                    ) : (
                      <span className="header-text">{headerColumnText}</span>
                    )}
                    <span className="sort-indicator">
                      {sortByHeader === 'asc' ? (
                        <ChevronUpIcon size="16px" />
                      ) : sortByHeader === 'desc' ? (
                        <ChevronDownIcon size="16px" />
                      ) : (
                        <span
                          style={{ width: '16px', display: 'inline-block' }}
                        ></span>
                      )}
                    </span>
                  </Flex>
                </TableHeaderCell>
                <TableHeaderCell
                  className="TableHeaderCell TableHeaderCell--change"
                  style={{ backgroundColor: getHeaderBgColor() }}
                  textAlign="right"
                  onClick={() => {
                    toggleSort('sortByChange');
                    scrollTableToTop();
                  }}
                >
                  <Flex
                    alignItems="center"
                    justifyContent="flex-end"
                    width="100%"
                  >
                    <span className="header-text">
                      Change ({chartCurrency})
                    </span>
                    <span className="sort-indicator">
                      {sortByChange === 'asc' ? (
                        <ChevronUpIcon size="16px" />
                      ) : sortByChange === 'desc' ? (
                        <ChevronDownIcon size="16px" />
                      ) : (
                        <span
                          style={{ width: '16px', display: 'inline-block' }}
                        ></span>
                      )}
                    </span>
                  </Flex>
                </TableHeaderCell>
                <TableHeaderCell
                  className="TableHeaderCell TableHeaderCell--percent-change"
                  style={{ backgroundColor: getHeaderBgColor() }}
                  textAlign="right"
                  onClick={() => {
                    toggleSort('sortByPercentage');
                    scrollTableToTop();
                  }}
                >
                  <Flex
                    alignItems="center"
                    justifyContent="flex-end"
                    width="100%"
                  >
                    <span className="header-text">Change (%)</span>
                    <span className="sort-indicator">
                      {sortByPercentage === 'asc' ? (
                        <ChevronUpIcon size="16px" />
                      ) : sortByPercentage === 'desc' ? (
                        <ChevronDownIcon size="16px" />
                      ) : (
                        <span
                          style={{ width: '16px', display: 'inline-block' }}
                        ></span>
                      )}
                    </span>
                  </Flex>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
          </div>
          <div
            className="nexus-table-body-container"
            ref={tableBodyRef}
            onWheel={handleWheel}
          >
            <TableBody>
              {/* Show data immediately if available */}
              {displayedData && displayedData?.length > 0 ? (
                // Display data rows
                displayedData.map((dat, index) => (
                  <TableRow key={index} variant="bottom-divider">
                    <TableCell className="TableCell TableCell--name">
                      <Button
                        variant="ghost"
                        justifyContent={'left'}
                        width={'100%'}
                        as={Link}
                        size={'xs'}
                        onClick={() => {
                          clickThru('cost', dat.name);
                        }}
                      >
                        <Text isTruncated className="table-cell-text">
                          {dat.name}
                        </Text>
                      </Button>
                    </TableCell>
                    <TableCell
                      textAlign="right"
                      className="TableCell TableCell--change"
                    >
                      {dat.change !== undefined
                        ? dat.change < 0
                          ? `-${chartCurrency}${Math.abs(dat.change)
                              .toString()
                              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
                          : `+${chartCurrency}${dat.change.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
                        : ''}
                    </TableCell>
                    <TableCell
                      textAlign="right"
                      className="TableCell TableCell--percent-change"
                    >
                      <Tag
                        variant={isWinsTable ? 'success' : 'danger'}
                        size="small"
                      >
                        {!isWinsTable && dat.per_change > 0 ? '+' : ''}
                        {dat.per_change}%
                      </Tag>
                    </TableCell>
                  </TableRow>
                ))
              ) : readyToShowNoData ? (
                // Show No Data message only after minimum loading time has elapsed
                <TableRow variant="clean">
                  <TableCell colSpan={3} textAlign="center">
                    <Flex
                      justifyContent="center"
                      alignItems="center"
                      height="200px"
                      width="100%"
                    >
                      <Text fontSize="lg" fontWeight="medium">
                        No Data
                      </Text>
                    </Flex>
                  </TableCell>
                </TableRow>
              ) : (
                // Show loading spinner while waiting for data or minimum time
                <TableRow variant="clean">
                  <TableCell
                    colSpan={3}
                    textAlign="center"
                    className="loading-spinner-cell"
                  >
                    <Flex justifyContent="center" alignItems="center">
                      <Spinner size="24px" />
                    </Flex>
                  </TableCell>
                </TableRow>
              )}

              {/* Intersection observer target for infinite scrolling */}
              {displayedData &&
                displayedData.length > 0 &&
                data &&
                data.length > itemsToShow && (
                  <TableRow variant="clean">
                    <TableCell
                      colSpan={3}
                      textAlign="center"
                      className="infinite-scroll-loader"
                    >
                      <div
                        ref={observerTarget}
                        className="infinite-scroll-observer"
                      />
                      {isLoadingMore && <Text>Loading more...</Text>}
                    </TableCell>
                  </TableRow>
                )}
            </TableBody>
          </div>
        </Table>
      </Box>
    </div>
  );

  // Render the original Chakra UI Table when showReskin is false
  const renderChakraTable = () => (
    <TableContainer maxWidth={'100%'}>
      <ChakraTable fontSize={'sm'} size={'sm'}>
        <Thead>
          <Tr>
            <Th
              onClick={() => toggleSort('sortByHeader')}
              style={{ cursor: 'pointer' }}
              width="50%" // Static width for Header column
            >
              {headerColumnText}{' '}
              {sortByHeader === 'asc' ? (
                <ChevronUpIcon size="16px" />
              ) : sortByHeader === 'desc' ? (
                <ChevronDownIcon size="16px" />
              ) : (
                ''
              )}
            </Th>
            <Th
              onClick={() => toggleSort('sortByPercentage')}
              style={{ cursor: 'pointer' }}
              width="25%" // Static width for % Change column
            >
              % Change{' '}
              {sortByPercentage === 'asc'
                ? '↑'
                : sortByPercentage === 'desc'
                  ? '↓'
                  : ''}
            </Th>
            <Th
              onClick={() => toggleSort('sortByChange')}
              style={{ cursor: 'pointer' }}
              width="25%" // Static width for Change column
            >
              {chartCurrency} Change{' '}
              {sortByChange === 'asc'
                ? '↑'
                : sortByChange === 'desc'
                  ? '↓'
                  : ''}
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {displayedData && displayedData?.length > 0 ? (
            // Display data rows
            displayedData.map((dat, index) => (
              <Tr key={index}>
                <Td width="50%" maxWidth={200}>
                  <Button
                    variant="ghost"
                    justifyContent={'left'}
                    width={'100%'}
                    as={Link}
                    size={'xs'}
                    onClick={() => {
                      clickThru('cost', dat.name);
                    }}
                  >
                    <Text isTruncated>{dat.name}</Text>
                  </Button>
                </Td>
                <Td width="25%">
                  {dat.per_change}% <Icon as={icon} />
                </Td>
                <Td width="25%">
                  <Icon as={icon} /> {chartCurrency}{' '}
                  {dat.change !== undefined
                    ? dat.change
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    : ''}
                </Td>
              </Tr>
            ))
          ) : readyToShowNoData ? (
            // Show No Data message after minimum loading time
            <Tr>
              <Td colSpan={3} textAlign="center" height="200px">
                <Text fontSize="lg" fontWeight="medium">
                  No Data
                </Text>
              </Td>
            </Tr>
          ) : (
            // Show loading spinner while waiting for data
            <Tr>
              <Td colSpan={3} textAlign="center" height="100px">
                <Flex justifyContent="center" alignItems="center">
                  <Spinner />
                </Flex>
              </Td>
            </Tr>
          )}
        </Tbody>
      </ChakraTable>
    </TableContainer>
  );

  // Render the original card header when showReskin is false
  const renderOriginalCardHeader = () => (
    <>
      <CardHeader textAlign={'center'} bgColor={bgColor}>
        <Flex alignItems={'center'} justifyContent={'center'}>
          <Flex
            gap={2}
            alignItems={'center'}
            flex={3}
            justifyContent={'center'}
          >
            <Icon as={headerIcon} />
            <Text fontSize={'md'}>{header}</Text>
          </Flex>
          <Flex
            justifyContent={'center'}
            alignItems={'flex-end'}
            flex={{ base: 2, sm: 1 }}
          >
            <Toggle
              checked={showOnlyTen}
              onChange={handleToggle}
              checkedChildren="10 rows"
              unCheckedChildren="All"
            />
          </Flex>
        </Flex>
      </CardHeader>
      <Divider mt={0} />
    </>
  );

  return (
    <>
      <Card width={'100%'} gap={0}>
        {showReskin ? null : renderOriginalCardHeader()}
        <CardBody>
          {showReskin ? renderNexusTable() : renderChakraTable()}
        </CardBody>
      </Card>
    </>
  );
};

export default TabularCard;
